import { useEffect, useRef, useState } from 'react'
import { useLocation, useMatch, useNavigate } from 'react-router-dom'
import { useEmmaUI, emmaEngine, Emma } from './Emma'
import { type EmmaSkin } from './emmaEngine'
import { emmaScreen, screenOffers, STARTERS, type Offer, type OfferAsk } from './emmaScreens'
import { useConversations, useSendToEmma, OutOfGrainError } from '@/hooks/useEmma'
import { useSeedBalance, EMMA_FREE_PER_WEEK } from '@/hooks/useSeeds'
import { useHeadlineDeck, useStories } from '@/hooks/useHeadlines'
import { useProfile } from '@/hooks/useProfile'
import { useAuthStore } from '@/store/auth'
import { useUIStore } from '@/store/ui'
import { ROUTES } from '@/components/gg/chrome/nav'
import { EmmaVoiceCard } from '@/components/voice/VoiceProfileCard'

const RESUME_MS = 24 * 60 * 60 * 1000
const THINK_MS = 700

type Turn =
  | { id: string; kind: 'offers'; list: Offer[]; first?: boolean }
  | { id: string; kind: 'me'; text: string }
  | { id: string; kind: 'em'; text: string; paid?: boolean }
  | { id: string; kind: 'thinking' }
  | { id: string; kind: 'failed'; text: string; retry?: { text: string; cmid: string } }

let seq = 0
const nid = () => `t${++seq}`

/**
 * Emma's sheet (Brian's emma-chat.js v3, Oct 22).
 *
 * A glass sheet over the bottom third of the screen; what you are asking about
 * stays in view above it. Emma sits large at its top left and asks the
 * question for the screen you are on, pulsing until you answer. Her two offers
 * for that screen are her first turn in the conversation; the one you pick is
 * replaced by the exchange, in the same box, so nothing shifts. The box at the
 * bottom takes your own question: one of the week's 5 included, then 1 seed
 * each until Monday. The offers are free. On a computer it is a glass window
 * in her corner.
 */
export function EmmaChat() {
  const open = useEmmaUI((s) => s.open)
  const setOpen = useEmmaUI((s) => s.setOpen)
  const [ever, setEver] = useState(false)
  useEffect(() => { if (open) setEver(true) }, [open])

  useEffect(() => {
    if (!open) return
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', esc)
    return () => document.removeEventListener('keydown', esc)
  }, [open, setOpen])

  return (
    <div className={`emma-sheet${open ? ' is-open' : ''}`} role="dialog" aria-label="Emma" aria-hidden={!open}>
      {ever && <SheetBody open={open} close={() => setOpen(false)} />}
    </div>
  )
}

function SheetBody({ open, close }: { open: boolean; close: () => void }) {
  const location = useLocation()
  const navigate = useNavigate()
  const storyMatch = useMatch('/app/story/:id')
  const storyId = storyMatch?.params.id ?? null
  const screen = emmaScreen(location.pathname)

  const client = useAuthStore((s) => s.client)
  const viewingAs = useUIStore((s) => s.viewingAs)
  const { data: profile } = useProfile()
  const { data: conversations } = useConversations()
  const { data: balance } = useSeedBalance()
  const { data: deck } = useHeadlineDeck()
  const { data: stories } = useStories()
  const send = useSendToEmma()

  const [conversationId, setConversationId] = useState<string | null>(null)
  const [chose, setChose] = useState(false)
  const [log, setLog] = useState<Turn[]>([])
  const [asking, setAsking] = useState(false)
  const [draft, setDraft] = useState('')
  const logRef = useRef<HTMLDivElement>(null)
  const landRef = useRef<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const typingTimer = useRef<number | null>(null)

  // Emma remembers the conversation she had today (her session), though the
  // sheet starts each visit with her offers.
  useEffect(() => {
    if (chose || !conversations) return
    const latest = conversations[0]
    if (latest && Date.now() - new Date(latest.updated_at).getTime() < RESUME_MS) setConversationId(latest.id)
    setChose(true)
  }, [conversations, chose])

  const free = balance?.emma_free_remaining ?? 0
  const freeOf = Math.max(EMMA_FREE_PER_WEEK, free)
  const canPay = free > 0 || (balance?.silo_remaining ?? 0) > 0 || (balance?.total_available ?? 0) > 0
  const note = free > 0
    ? `${free} of ${freeOf} questions left this week`
    : 'This week’s questions are used; 1 seed each until Monday'

  const talked = log.some((t) => t.kind === 'me')

  // Opening: the screen's question; her offers as her first turn. Until you
  // have said anything, her offers follow the screen you are on.
  useEffect(() => {
    if (!open) return
    setLog((l) => {
      if (l.some((t) => t.kind === 'me')) return l
      return [{ id: nid(), kind: 'offers', list: screenOffers(screen), first: true }]
    })
    if (!talked) {
      setAsking(true)
      emmaEngine()?.ask()
    }
    if (window.matchMedia?.('(pointer: fine)').matches) window.setTimeout(() => inputRef.current?.focus(), 320)
  }, [open, screen.key]) // eslint-disable-line react-hooks/exhaustive-deps

  // The log lands on her answer, not past it (v3).
  useEffect(() => {
    const el = logRef.current
    if (!el) return
    const id = landRef.current
    const target = id ? el.querySelector<HTMLElement>(`[data-turn="${id}"]`) : null
    if (target) { el.scrollTop = Math.max(0, target.offsetTop - el.offsetTop - 4); landRef.current = null }
    else el.scrollTop = el.scrollHeight
  }, [log])

  function answered() { setAsking(false) }

  /** She thinks, then talks. */
  function reply(turns: Turn[]) {
    const think: Turn = { id: nid(), kind: 'thinking' }
    setLog((l) => [...l, think])
    emmaEngine()?.sent()
    window.setTimeout(() => {
      landRef.current = turns[0]?.id ?? null
      setLog((l) => [...l.filter((t) => t.id !== think.id), ...turns])
      emmaEngine()?.answered()
    }, THINK_MS)
  }

  const say = (text: string): Turn => ({ id: nid(), kind: 'em', text })
  const offer = (list: Offer[]): Turn => ({ id: nid(), kind: 'offers', list })

  function builtStories() {
    return (stories ?? []).filter((s) => s.status !== 'skipped').slice(0, 3)
  }

  function ask(kind: OfferAsk, label: string, from: string) {
    answered()
    setLog((l) => [...l.filter((t) => t.id !== from), { id: nid(), kind: 'me', text: label }])
    if (kind === 'build') {
      const it = (deck ?? []).slice(0, 3)
      if (!it.length) {
        reply([say('Your list is empty right now. Pull 5 more at the end of your feed and I’ll point out the strongest.'),
          offer([{ t: 'Go to your feed', go: ROUTES.feed, pri: true }])])
        return
      }
      reply([say('These are the strongest in your list right now. Open one to read what’s been reported, then tap Build and I’ll write it in your voice.'),
        offer(it.map((h, i) => ({ t: h.headline, s: [h.coverage === 'original' ? 'Public record' : null, h.place, h.source_name].filter(Boolean).join(' · '), pri: i === 0, go: `${ROUTES.feed}?focus=${encodeURIComponent(h.id)}` })))])
    } else if (kind === 'rewrite') {
      const b = builtStories()
      if (!b.length) {
        reply([say('Build a story first and I can help rewrite it: tighter, warmer, or from another position.'),
          offer([{ t: 'Pick a story to build', ask: 'build', pri: true }])])
        return
      }
      reply([say('Which one? I’ll open it with the script. Tap Rewrite under it and tell me what to change: shorter, a stronger hook, or another position.'),
        offer(b.map((x, i) => ({ t: x.headline, pri: i === 0, go: ROUTES.story(x.id) })))])
    } else if (kind === 'record') {
      const b = builtStories()
      if (!b.length) {
        reply([say('Nothing’s waiting to record yet. Build a story from your feed and it lands here, ready for the camera.'),
          offer([{ t: 'Go to your feed', go: ROUTES.feed, pri: true }])])
        return
      }
      reply([say('These are built and waiting. I’d start with the first: it’s the freshest.'),
        offer(b.map((x, i) => ({ t: x.headline, pri: i === 0, go: `${ROUTES.record}?story=${encodeURIComponent(x.id)}` })))])
    } else {
      reply([say('What do you want more or less of? Each of these shapes what your brief pulls.'),
        offer([
          { t: 'Topics and keywords', s: 'The mix, and the pills that put named things first', go: ROUTES.topics, pri: true },
          { t: 'Places', s: 'The towns and counties you cover', go: ROUTES.places },
          { t: 'Watching', s: 'Breaking news you want the moment it happens', go: ROUTES.breaking },
          { t: 'Public records', s: 'Licenses, permits and agendas for your places', go: ROUTES.recordsSettings },
        ])])
    }
  }

  function pick(o: Offer, from: string) {
    if (o.go) { close(); navigate(o.go); return }
    if (o.ask) ask(o.ask, o.t, from)
  }

  async function typed(text: string, retryId?: string) {
    const message = text.trim()
    if (!message || send.isPending) return
    answered()
    setDraft('')
    if (typingTimer.current) window.clearTimeout(typingTimer.current)
    if (!retryId) setLog((l) => [...l, { id: nid(), kind: 'me', text: message }])
    if (!canPay) {
      reply([say(`You’ve used this week’s ${EMMA_FREE_PER_WEEK} questions and there are no seeds left for another. More come Monday. Or pick one of these, they’re free.`), offer(STARTERS)])
      return
    }
    const cmid = retryId ?? crypto.randomUUID()
    const think: Turn = { id: nid(), kind: 'thinking' }
    setLog((l) => [...l.filter((t) => !(t.kind === 'failed' && t.retry?.cmid === cmid)), think])
    emmaEngine()?.sent()
    try {
      const r = await send.mutateAsync({
        message, conversationId, clientMessageId: cmid, storyId,
        screen: { key: screen.key, name: screen.name, path: location.pathname },
      })
      setConversationId(r.conversation_id)
      const ans: Turn = { id: nid(), kind: 'em', text: r.reply, paid: r.paid_with === 'silo' || r.paid_with === 'seeds' }
      landRef.current = ans.id
      setLog((l) => [...l.filter((t) => t.id !== think.id), ans])
      emmaEngine()?.answered()
    } catch (err) {
      const oog = err instanceof OutOfGrainError
      setLog((l) => [...l.filter((t) => t.id !== think.id),
        oog
          ? { id: nid(), kind: 'em', text: `You’ve used this week’s ${EMMA_FREE_PER_WEEK} questions and there are no seeds left for another. More come Monday. Or pick one of these, they’re free.` }
          : { id: nid(), kind: 'failed', text: (err as Error).message || 'That did not reach Emma. You were not charged.', retry: { text: message, cmid } },
        ...(oog ? [offer(STARTERS)] : []),
      ])
      emmaEngine()?.typingStopped()
    }
  }

  function onType(v: string) {
    setDraft(v)
    emmaEngine()?.typing()
    if (typingTimer.current) window.clearTimeout(typingTimer.current)
    typingTimer.current = window.setTimeout(() => emmaEngine()?.typingStopped(), 1400)
  }

  // A swipe down on the sheet's top closes it.
  const y0 = useRef<number | null>(null)

  const displayClient = viewingAs ?? client

  return (
    <>
      <div
        className="emma-sheet__top"
        onTouchStart={(e) => { y0.current = e.touches[0].clientY }}
        onTouchEnd={(e) => { if (y0.current != null && e.changedTouches[0].clientY - y0.current > 50) close(); y0.current = null }}
      >
        <div className="emma-sheet__grab" aria-hidden="true" />
        <div className="emma-sheet__hd">
          <div className={`emma-sheet__av${asking ? ' is-asking' : ''}`}>
            <Emma variant="sheet" color={profile?.emma_color} skin={(profile?.emma_skin as EmmaSkin | null) ?? 'seed'} accent={displayClient?.accent_color} />
          </div>
          <div className="emma-sheet__qa">
            <p className="emma-sheet__q">{screen.q}</p>
            <small className={!canPay ? 'is-empty' : ''}>{note}</small>
          </div>
          <button type="button" className="emma-sheet__x" onClick={close} aria-label="Close">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
      </div>

      {/* The voice ring's card while it isn't full (voice-ring.js, Oct 31) */}
      <EmmaVoiceCard />

      <div className="emma-sheet__log" ref={logRef} aria-live="polite">
        {log.map((t) => {
          if (t.kind === 'offers') {
            return (
              <div key={t.id} data-turn={t.id} className="emma-sheet__opts">
                {t.list.map((o, i) => (
                  <button key={i} type="button" className={`emma-sheet__opt${o.pri ? ' is-pri' : ''}`} onClick={() => pick(o, t.id)}>
                    {o.t}{o.s && <small>{o.s}</small>}
                  </button>
                ))}
              </div>
            )
          }
          if (t.kind === 'me') return <div key={t.id} data-turn={t.id} className="emma-sheet__m is-me">{t.text}</div>
          if (t.kind === 'thinking') return <div key={t.id} className="emma-sheet__m is-em is-typing">Emma is thinking…</div>
          if (t.kind === 'failed') {
            return (
              <div key={t.id} data-turn={t.id} className="emma-sheet__m is-em is-failed">
                {t.text}
                {t.retry && <button type="button" className="emma-sheet__retry" onClick={() => void typed(t.retry!.text, t.retry!.cmid)}>Try again</button>}
              </div>
            )
          }
          return (
            <div key={t.id} data-turn={t.id} className="emma-sheet__m is-em">
              {t.text}
              {t.paid && <small>1 seed</small>}
            </div>
          )
        })}
      </div>

      <form className="emma-sheet__in" onSubmit={(e) => { e.preventDefault(); void typed(draft) }}>
        <input
          ref={inputRef}
          type="text"
          value={draft}
          placeholder="Or ask your own"
          aria-label="Ask Emma"
          autoComplete="off"
          maxLength={2000}
          onChange={(e) => onType(e.target.value)}
          onBlur={() => emmaEngine()?.typingStopped()}
          disabled={!open}
        />
        <button type="submit" aria-label="Send" disabled={!draft.trim() || send.isPending}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
        </button>
      </form>
    </>
  )
}
